/**
 * @file InvoiceBookOverDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Pricing & Costing's Invoice window (Cam, 3 Nov 2026 —
 *              "Booking over: need to be able to easily change the invoice
 *              date and due date to a different month than the order, adding a
 *              note to the sales description (manual entry) for when users need
 *              to book over an order", and "Booking over can often need
 *              splitting over multiple months so this workflow needs a modal
 *              asking user for info then the system actions it for them").
 *
 *   Opens before every Invoice action (a row's "Invoice SO-D…", the ticked
 *   rows', the right-click's, a product group's) — one window for the lot —
 *   in place of the plain "Invoice orders?" question, whose words it shows.
 *
 *   Invoice date:
 *   - "The delivery date" (as before, the default) — nothing changes for
 *     anyone who just presses Invoice.
 *   - "Book over to another date": Invoice date (never on or before Xero's
 *     lock — useXeroLock; refused on the server too), Due date (the 18th of
 *     the month after the invoice date until changed; never before the
 *     invoice date) and the note added to every line's description (typed,
 *     required, one line, at most 200 characters). Several orders: one
 *     date and one note for them all; "{delivery date}" in the note becomes
 *     each order's own delivery date (said under the box).
 *   - One order: "Split into" 1–12 invoices. Two or more: a row per part —
 *     invoice date (the 1st of each month after delivery in turn), due date,
 *     note ("Delivered 15/10/2026 — booked over, part 1 of 3") — and each
 *     line's quantity on each part (an equal split, the last part taking the
 *     remainder), each part's net / VAT / total as it will be invoiced, and
 *     whether the parts add up to the whole order. The order's lines come
 *     from GET /api/pricing-costing/invoice/book-over/:orderId. Splitting
 *     needs migrations/invoice_booked_over.sql — greyed "Not ready" until it
 *     has run.
 *
 *   The button is greyed with the first thing in the way (the rules are
 *   utils/bookOver.bookOverProblems, which the server applies again).
 *   onConfirm(bookOver) — null for the delivery date, else
 *   { parts: [{ invoice_date, due_date, note, lines? }] }.
 *
 *   Props: { orders: [{ id, number, deliveryDate }], message, confirmText,
 *   onCancel, onConfirm }.
 *
 *   1.1.0 (2026-11-03, Cam — "split one invoice across dates or move it after
 *   it has been invoiced", "so long as the invoice isn't paid"): `invoiceId`
 *   opens the same window for an invoice ALREADY raised (BookOverInvoiceFlow —
 *   Search Invoices, the invoice window, Invoice Detail's variation review,
 *   Pricing & Costing's invoiced rows). It starts on "Book over" (no
 *   delivery-date choice), reads GET /api/invoices/:id/book-over (the booking:
 *   which invoice(s) it replaces, kept or voided, sent to the customer or not;
 *   its lines; why it can't be booked over or split), says "Replaces …" and,
 *   for one that has gone out, that the customer will need the new invoice(s).
 *   onConfirm(bookOver, { expected }) — the booking's invoice ids as shown.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import useXeroLock from '../hooks/useXeroLock';
import { API_URL } from '../config';
import { formatQuantity } from '../utils/numberFormatters';
import { money as priceText } from '../utils/pricingCosting';
import {
  BOOK_OVER_MAX_PARTS, BOOK_OVER_NOTE_MAX, DELIVERY_DATE_TOKEN, SPLIT_NOT_READY,
  defaultParts, dueDateFor, defaultNote, bookOverProblems, partTotals, partQty, round4, ukDay, monthWords
} from '../utils/bookOver';
import './InvoiceBookOverDialog.css';

// Totals to the penny (prices to 4 places, as the screen shows them — priceText).
const money = (n) => `£${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The window's parts as the request sends them: quantities as numbers. */
export function bookOverPayload(parts) {
  return {
    parts: (parts || []).map(p => ({
      invoice_date: p.invoice_date,
      due_date: p.due_date,
      note: String(p.note || '').trim(),
      ...(p.lines ? { lines: Object.fromEntries(Object.entries(p.lines).map(([id, q]) => [id, partQty(p, id)])) } : {})
    }))
  };
}

function InvoiceBookOverDialog({ orders = [], invoiceId = null, message = '', error = null, confirmText = 'Invoice', busy = false, onCancel, onConfirm }) {
  const { firstAllowedDate, effectiveLockDate } = useXeroLock();
  // An invoice already raised (1.1.0): one booking, booked over — no delivery-date choice.
  const existingMode = invoiceId != null;
  const single = existingMode || orders.length === 1;
  const [mode, setMode] = useState(existingMode ? 'book' : 'delivery');
  const [count, setCount] = useState(1);
  const [parts, setParts] = useState([]);
  // Per part: has the person typed the due date / the note (then a date
  // change leaves them alone)?
  const [touched, setTouched] = useState([]);
  const [info, setInfo] = useState({ loading: false, error: null, data: null });

  // The latest delivery among the orders (one order: the server's, which is
  // the redelivery's day once redelivered).
  const deliveryDate = useMemo(() => {
    if (single && info.data?.delivery_date) return info.data.delivery_date;
    return orders.map(o => String(o.deliveryDate || '').slice(0, 10)).filter(Boolean).sort().pop() || null;
  }, [orders, single, info.data]);
  const lines = useMemo(() => info.data?.lines || [], [info.data]);
  const splitReady = !!(info.data && info.data.split_ready && !info.data.split_block);
  const noteWhat = info.data?.note_what || 'Delivered';
  const splitWhy = !single ? 'Splitting is for one order at a time — invoice it on its own to split it'
    : info.loading ? 'Reading the order…'
      : info.error ? info.error
        : info.data ? (info.data.split_ready ? info.data.split_block : SPLIT_NOT_READY) : null;

  // One order: read its lines (and the delivery date the note names) the first
  // time booking over is chosen.
  // Read once per window (a ref, so the read's own state change cannot cancel it).
  const asked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (mode !== 'book' || !single || asked.current) return;
    asked.current = true;
    setInfo({ loading: true, error: null, data: null });
    const url = existingMode
      ? `${API_URL}/api/invoices/${invoiceId}/book-over`
      : `${API_URL}/api/pricing-costing/invoice/book-over/${orders[0].id}`;
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.success) throw new Error(body.error || (existingMode ? 'The invoice could not be read' : 'The order could not be read'));
        if (mounted.current) setInfo({ loading: false, error: null, data: body });
      })
      .catch((err) => { if (mounted.current) setInfo({ loading: false, error: err.message, data: null }); });
  }, [mode, single, orders, existingMode, invoiceId]);

  // Fresh parts when booking over starts or the split changes; when only the
  // order's own delivery date, its lines or Xero's lock arrive, what the
  // person typed (a due date, a note) is kept. Keyed on the facts, not on
  // array identities, so a re-render never resets them.
  const linesKey = count > 1 ? lines.map(l => `${l.order_line_id}:${l.quantity}`).join(',') : '';
  const shapeKey = `${mode}|${count}`;
  const factsKey = `${deliveryDate || ''}|${firstAllowedDate || ''}|${single}|${linesKey}|${noteWhat}`;
  const lastShape = useRef(null);
  useEffect(() => {
    if (mode !== 'book') { lastShape.current = null; return; }
    const fresh = defaultParts({ deliveryDate, count, lines, firstAllowedDate, many: !single, what: noteWhat });
    const sameShape = lastShape.current === shapeKey;
    lastShape.current = shapeKey;
    if (!sameShape) {
      setParts(fresh);
      setTouched(Array.from({ length: count }, () => ({ due: false, note: false })));
      return;
    }
    setParts(prev => fresh.map((p, i) => {
      const was = prev[i];
      const t = touched[i] || {};
      if (!was) return p;
      return { ...p, ...(t.due ? { due_date: was.due_date } : {}), ...(t.note ? { note: was.note } : {}) };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeKey, factsKey]);

  const setPart = (i, patch, touch = null) => {
    setParts(prev => prev.map((p, k) => (k === i ? { ...p, ...patch } : p)));
    if (touch) setTouched(prev => prev.map((t, k) => (k === i ? { ...t, [touch]: true } : t)));
  };
  // A new invoice date moves the due date and the one-part note with it, until
  // they have been typed.
  const setInvoiceDate = (i, day) => {
    const t = touched[i] || {};
    const patch = { invoice_date: day };
    if (!t.due) patch.due_date = day ? dueDateFor(day) : '';
    if (!t.note && count === 1) patch.note = defaultNote({ deliveryDate, invoiceDate: day, many: !single, what: noteWhat });
    setPart(i, patch);
  };
  const setLineQty = (i, id, value) => {
    setParts(prev => prev.map((p, k) => (k === i ? { ...p, lines: { ...(p.lines || {}), [String(id)]: value } } : p)));
  };

  const problems = useMemo(() => (mode !== 'book' ? [] : bookOverProblems({
    parts, lines: count > 1 ? lines : null, firstAllowedDate
  })), [mode, parts, lines, count, firstAllowedDate]);
  const waitingForLines = mode === 'book' && (count > 1 || existingMode) && !info.data;
  const blocked = busy ? 'Saving…'
    : mode === 'book' && (waitingForLines ? (info.error || (existingMode ? 'Reading the invoice…' : 'Reading the order…'))
      : ((existingMode && info.data?.block) || problems[0] || null));

  const totalsOf = useMemo(() => parts.map(p => partTotals(p, lines)), [parts, lines]);
  const whole = useMemo(() => partTotals({ lines: null }, lines), [lines]);
  const partsSum = totalsOf.reduce((a, t) => ({ net: a.net + t.net, vat: a.vat + t.vat, total: a.total + t.total }), { net: 0, vat: 0, total: 0 });

  const submit = () => {
    if (mode !== 'book') { onConfirm(null); return; }
    if (blocked) return;
    if (existingMode) onConfirm(bookOverPayload(parts), { expected: (info.data?.existing || []).map(e => e.id) });
    else onConfirm(bookOverPayload(parts));
  };

  const buttonLabel = mode !== 'book' ? confirmText
    : existingMode ? (count > 1 ? `Split into ${count} invoices` : 'Book over')
    : count > 1 ? `Raise ${count} invoices`
      : orders.length > 1 ? `Book over ${orders.length}` : 'Book over';

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onCancel}>
          <div className="icon-wrapper"><span className="material-symbols-rounded" aria-hidden="true">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={submit} disabled={!!blocked} title={blocked || ''} data-testid="book-over-confirm">
          <div className="icon-wrapper"><span className="material-symbols-rounded" aria-hidden="true">{mode === 'book' ? 'event_repeat' : 'receipt_long'}</span></div>
          <span className="btn-label">{buttonLabel}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={onCancel}
      title={existingMode ? `Book over ${info.data?.invoice?.invoice_no || 'invoice'}` : (single ? 'Invoice order' : 'Invoice orders')}
      size={mode === 'book' && count > 1 ? 'xl' : 'md'}
      className="book-over" footer={footer}>
      <BookOverBody
        mode={mode} setMode={setMode} count={count} setCount={setCount} single={single} message={message} error={error}
        parts={parts} setPart={setPart} setInvoiceDate={setInvoiceDate} setLineQty={setLineQty} touched={touched}
        firstAllowedDate={firstAllowedDate} effectiveLockDate={effectiveLockDate} deliveryDate={deliveryDate} orders={orders}
        info={info} lines={lines} splitReady={splitReady} splitWhy={splitWhy}
        problems={problems} totalsOf={totalsOf} whole={whole} partsSum={partsSum} buttonLabel={buttonLabel}
        existingMode={existingMode}
      />
    </Modal>
  );
}

function BookOverBody({
  mode, setMode, count, setCount, single, message, error, parts, setPart, setInvoiceDate, setLineQty,
  firstAllowedDate, effectiveLockDate, deliveryDate, orders, info, lines, splitReady, splitWhy,
  problems, totalsOf, whole, partsSum, buttonLabel, existingMode
}) {
  useSlideOutButton([mode, count, buttonLabel]);
  const countOptions = Array.from({ length: BOOK_OVER_MAX_PARTS }, (_, i) => ({
    value: i + 1, label: i === 0 ? '1 invoice' : `${i + 1} invoices`, disabled: i > 0 && !splitReady,
    title: i > 0 && !splitReady ? (splitWhy || undefined) : undefined
  }));
  const p0 = parts[0] || {};
  const addsUp = Math.abs(partsSum.total - whole.total) < 0.005;
  return (
    <div className="bo-body">
      {message && <p className="bo-message">{message}</p>}
      {error && <p className="bo-block" role="alert">{error}</p>}

      {existingMode && <ReplacesBlock info={info} count={count} />}

      {!existingMode && (
      <div className="bo-choice" role="radiogroup" aria-label="Invoice date">
        <button type="button" role="radio" aria-checked={mode === 'delivery'} className={`bo-option${mode === 'delivery' ? ' is-on' : ''}`}
          onClick={() => setMode('delivery')}>
          <span className="material-symbols-rounded" aria-hidden="true">local_shipping</span>
          <span><strong>The delivery date</strong>
            <small>As now: {single ? 'the invoice is' : 'each invoice is'} dated its order's delivery date (the first open day if Xero has locked it), due the 18th of the month after.</small></span>
        </button>
        <button type="button" role="radio" aria-checked={mode === 'book'} className={`bo-option${mode === 'book' ? ' is-on' : ''}`}
          onClick={() => setMode('book')}>
          <span className="material-symbols-rounded" aria-hidden="true">event_repeat</span>
          <span><strong>Book over to another date</strong>
            <small>Date {single ? 'the invoice' : 'every invoice'} in another month than the delivery, with a note on every line{single ? ' — or split it over several months' : ''}.</small></span>
        </button>
      </div>
      )}

      {mode === 'book' && (
        <div className="bo-book">
          {single && (
            <div className="bo-split">
              <label>Split into</label>
              <div className="bo-split-pick" title={!splitReady ? (splitWhy || '') : ''}>
                <Dropdown options={countOptions} value={count} onChange={(v) => setCount(Number(v) || 1)} />
              </div>
              {info.loading && <LoadingSpinner size="small" />}
              {!info.loading && !splitReady && splitWhy && <span className="bo-hint">{splitWhy}</span>}
              {count > 1 && <span className="bo-hint">One invoice a month: each part carries its share of every line, at the line's own price.</span>}
            </div>
          )}

          {count === 1 && (
            <div className="bo-fields">
              <div className="bo-field">
                <label>Invoice date</label>
                <DateInput value={p0.invoice_date || ''} min={firstAllowedDate || undefined} onChange={(e) => setInvoiceDate(0, e.target.value)} />
                {firstAllowedDate && (
                  <small className="bo-hint">
                    {effectiveLockDate ? `Xero is locked to ${ukDay(effectiveLockDate)} — ` : ''}{ukDay(firstAllowedDate)} or later.
                  </small>
                )}
              </div>
              <div className="bo-field">
                <label>Due date</label>
                <DateInput value={p0.due_date || ''} min={p0.invoice_date || undefined} onChange={(e) => setPart(0, { due_date: e.target.value }, 'due')} />
                <small className="bo-hint">The 18th of the month after the invoice date unless changed.</small>
              </div>
              <div className="bo-field bo-note">
                <label>Note added to each line's description</label>
                <Input value={p0.note || ''} maxLength={BOOK_OVER_NOTE_MAX} placeholder="Why it is booked over"
                  onChange={(e) => setPart(0, { note: e.target.value }, 'note')} aria-label="Note added to each line's description" />
                <small className="bo-hint">
                  {existingMode
                    ? `Goes after each line's words on the invoice (an earlier booking's note is replaced). ${String(p0.note || '').length} of ${BOOK_OVER_NOTE_MAX}.`
                    : single
                    ? `Goes after each line's words on the invoice only (the order's words don't change). ${String(p0.note || '').length} of ${BOOK_OVER_NOTE_MAX}.`
                    : `Goes after each line's words on every invoice. "${DELIVERY_DATE_TOKEN}" becomes each order's own delivery date (${orders.slice(0, 3).map(o => `${o.number} ${ukDay(o.deliveryDate)}`).join(', ')}${orders.length > 3 ? ', …' : ''}). ${String(p0.note || '').length} of ${BOOK_OVER_NOTE_MAX}.`}
                </small>
              </div>
            </div>
          )}

          {count > 1 && info.data && (
            <>
              <table className="data-table bo-parts">
                <thead>
                  <tr><th>Part</th><th>Invoice date</th><th>Due date</th><th>Note added to each line</th><th className="num">Net</th><th className="num">VAT</th><th className="num">Total</th></tr>
                </thead>
                <tbody>
                  {parts.map((p, i) => (
                    <tr key={`p${i}`}>
                      <td>{i + 1} of {count}<small className="bo-month">{monthWords(p.invoice_date)}</small></td>
                      <td><DateInput value={p.invoice_date || ''} min={firstAllowedDate || undefined} onChange={(e) => setInvoiceDate(i, e.target.value)} /></td>
                      <td><DateInput value={p.due_date || ''} min={p.invoice_date || undefined} onChange={(e) => setPart(i, { due_date: e.target.value }, 'due')} /></td>
                      <td><Input value={p.note || ''} maxLength={BOOK_OVER_NOTE_MAX} onChange={(e) => setPart(i, { note: e.target.value }, 'note')} aria-label={`Part ${i + 1} note`} /></td>
                      <td className="num">{money(totalsOf[i]?.net)}</td>
                      <td className="num">{money(totalsOf[i]?.vat)}</td>
                      <td className="num">{money(totalsOf[i]?.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={addsUp ? '' : 'bo-off'}>
                    <td colSpan={4}>{addsUp ? 'The parts add up to the whole order' : `The parts come to ${money(partsSum.total)} — the whole order is ${money(whole.total)}`}</td>
                    <td className="num">{money(partsSum.net)}</td>
                    <td className="num">{money(partsSum.vat)}</td>
                    <td className="num">{money(partsSum.total)}</td>
                  </tr>
                </tfoot>
              </table>

              <table className="data-table bo-lines">
                <thead>
                  <tr>
                    <th>Line</th><th className="num">Invoices</th><th className="num">Price</th>
                    {parts.map((_, i) => <th key={`h${i}`} className="num">Part {i + 1}</th>)}
                    <th className="num">Parts</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => {
                    const sum = round4(parts.reduce((s, p) => s + (partQty(p, l.order_line_id) || 0), 0));
                    const off = Math.abs(sum - round4(l.quantity)) > 0.00005;
                    return (
                      <tr key={l.order_line_id} className={off ? 'bo-off' : ''}>
                        <td>{l.description || l.item_code}</td>
                        <td className="num">{formatQuantity(l.quantity)}</td>
                        <td className="num">{priceText(l.unit_price)}</td>
                        {parts.map((p, i) => (
                          <td key={`q${i}`} className="num">
                            <Input type="number" step="any" min="0" value={p.lines?.[String(l.order_line_id)] ?? ''}
                              aria-label={`${l.description || l.item_code} on part ${i + 1}`}
                              onChange={(e) => setLineQty(i, l.order_line_id, e.target.value)} />
                          </td>
                        ))}
                        <td className="num">{formatQuantity(sum)}{off ? ` (${formatQuantity(round4(l.quantity - sum))} left)` : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <small className="bo-hint">
                Split equally, the last part taking the remainder, so they add up exactly; whole bags and totes where the line is counted in them.
                Each line on each part at its confirmed price and VAT; a part with 0 of a line leaves it off that invoice.
                {deliveryDate ? ` Delivered ${ukDay(deliveryDate)}.` : ''}
              </small>
            </>
          )}
          {count > 1 && !info.data && info.loading && <LoadingSpinner />}

          {problems.length > 0 && (
            <ul className="bo-problems" aria-live="polite">
              {problems.slice(0, 6).map((p, i) => <li key={`e${i}`}>{p}</li>)}
              {problems.length > 6 && <li>and {problems.length - 6} more</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What an existing invoice's booking is, and what booking it over does to it
 * (1.1.0): the first invoice is kept and changed (its number stays), any other
 * part of an earlier booking is voided, new invoices are raised for parts 2…N.
 * Sent to the customer already: said, with what to do.
 */
function ReplacesBlock({ info, count }) {
  if (info.loading) return <LoadingSpinner size="small" />;
  if (info.error) return <p className="bo-block">{info.error}</p>;
  const d = info.data;
  if (!d) return null;
  const list = d.existing || [];
  const kept = list[0];
  const goneOut = list.filter(e => e.gone_out);
  const others = list.slice(1);
  return (
    <div className="bo-replaces" data-testid="book-over-replaces">
      {d.block && <p className="bo-block">{d.block}</p>}
      <p>
        {list.length > 1 ? 'Replaces the booking ' : 'Replaces '}
        {list.map((e, i) => (
          <span key={e.id}>{i ? ', ' : ''}<strong>{e.invoice_no}</strong> dated {ukDay(e.invoice_date)}, due {ukDay(e.due_date)} ({money(e.total)})</span>
        ))}
        {d.order ? ` — ${d.order.order_number}'s invoice` : ' — made by hand, no order'}.
      </p>
      {kept && (
        <p className="bo-hint">
          {kept.invoice_no} is kept and changed{count > 1 ? ' to part 1' : ''} — its number stays.
          {count > 1 ? ` ${count - 1} new invoice${count - 1 === 1 ? ' is' : 's are'} raised for the other part${count - 1 === 1 ? '' : 's'}.` : ''}
          {others.length ? ` ${others.map(e => e.invoice_no).join(', ')} ${others.length === 1 ? 'is' : 'are'} voided${others.some(e => e.in_xero) ? ' (in Xero too)' : ''}.` : ''}
          {d.order ? ` Recorded on ${d.order.order_number}.` : ''}
        </p>
      )}
      {goneOut.length > 0 && (
        <p className="bo-warning" data-testid="book-over-gone-out">
          {goneOut.map(e => e.gone_out).join('; ')} — already sent to the customer, so they will need the new invoice{count > 1 || list.length > 1 ? 's' : ''}. You'll be offered to send {count > 1 ? 'them' : 'it'} afterwards.
        </p>
      )}
    </div>
  );
}

export default InvoiceBookOverDialog;
